<script setup lang="ts">
// ================================
// Optim TURPE v2 — état de chargement « vivant » (DEV-762).
// Le calcul (recherche de structure tarifaire + chiffrage) prend moins d'une minute (contrôle du
// 05/10 : médiane 28,6 s, pointes à 65 et 89 s) : au lieu d'une simple
// ligne de texte, on rassure avec un en-tête animé + des étapes qui s'enchaînent + un squelette du
// résultat (shimmer) pour que la page ne saute pas quand l'étude arrive.
// Maquette mq-attente (« maquette 7 », votée le 05/10/2026) : la durée
// habituelle, la page peut être quittée (vrai depuis le 02/10 : l'ouverture
// reprend le suivi d'un calcul en file au lieu d'en relancer un), et le
// montant de la dernière étude de l'ancienne version quand il y en a une.
// Seconde étape de la maquette : dès 60 s d'affichage (minuteur de l'écran
// seul, aucune donnée du store), le calcul est dit plus long que d'habitude,
// pour que « moins d'une minute » ne reste jamais affiché au-delà. La page
// donne au composant une clé liée au compteur : changer de compteur pendant
// un calcul le remonte, et le compte repart de zéro. Pas de bouton « Choisir un autre compteur » : la page n'a pas
// d'action de retour au choix du compteur à réutiliser (le sélecteur reste
// au-dessus). Le titre « Première étude… » et l'heure de départ sont reportés.
// ================================
import { computed, onMounted, onUnmounted, ref } from "vue"
import { useI18n } from "vue-i18n"

// ================================
// PROPS
// ================================
type Props = {
    /** La ligne de l'ancienne version et sa suite (`lireAttenteAncienneVersion`), ou `null`. */
    ancienneVersion?: { ligne: string; suite: string } | null
    /**
     * Le lancement a-t-il été accepté avec un identifiant d'étude (DEV-2412) ?
     * `true` : l'étude se suit au retour, la carte peut le promettre.
     * `false` : le lancement part, la carte dit « Lancement de l'étude… »,
     * sans promesse. `null` : aucun lancement (chargement de la page), aucune
     * promesse non plus.
     */
    lancementAccepte?: boolean | null
}
const props = withDefaults(defineProps<Props>(), {
    ancienneVersion: null,
    lancementAccepte: null,
})

const { t } = useI18n({
    locale: "fr",
    messages: {
        fr: {
            title: "Étude de ce compteur en cours",
            // DEV-2412 : tant que le lancement n'est pas accepté.
            titreLancement: "Lancement de l'étude…",
            subtitle: "Durée habituelle : moins d'une minute",
            // K1 (14/10/2026) : la carte seule dit l'attente, et que l'écran
            // se met à jour de lui-même.
            quitter:
                "L'écran se met à jour dès que l'étude aboutit. Vous pouvez quitter la page : l'étude sera affichée à votre retour.",
            plusLong:
                "Le calcul est plus long que d'habitude. Vous pouvez quitter la page : l'étude sera affichée à votre retour.",
            // DEV-2412 : les mêmes phrases sans la promesse, tant que le
            // lancement n'est pas accepté avec un identifiant d'étude.
            quitterSansPromesse:
                "L'écran se met à jour dès que l'étude aboutit.",
            plusLongSansPromesse: "Le calcul est plus long que d'habitude.",
            steps: {
                // K3 : les étapes du calcul dans les mots de la partie 4.
                s1: "Lecture des mesures",
                s2: "Analyse des pics de puissance",
                s3: "Comparaison des formules et des puissances",
                s4: "Calcul de l'économie",
            },
        },
    },
})

const STEPS = ["s1", "s2", "s3", "s4"] as const

/** Le titre : « Lancement de l'étude… » tant que le lancement n'est pas accepté. */
const getTitre = computed(() =>
    props.lancementAccepte === false ? t("titreLancement") : t("title")
)
/** La phrase d'attente : la promesse du retour seulement après l'acceptation. */
const getPhraseAttente = computed(() => {
    const promesse = props.lancementAccepte === true
    if (plusLong.value)
        return promesse ? t("plusLong") : t("plusLongSansPromesse")
    return promesse ? t("quitter") : t("quitterSansPromesse")
})
/** Étape mise en avant ; avance toutes les 6 s (~24 s pour les 4) puis reste sur la dernière. */
const current = ref(0)
let timer: ReturnType<typeof setInterval> | undefined

/** Dès cette durée d'affichage, le calcul est plus long que d'habitude (« moins d'une minute »). */
const DUREE_HABITUELLE_DEPASSEE_MS = 60_000
/** Vrai une fois la durée habituelle dépassée. */
const plusLong = ref(false)
let minuteurPlusLong: ReturnType<typeof setTimeout> | undefined

onMounted(() => {
    timer = setInterval(() => {
        if (current.value < STEPS.length - 1) current.value++
    }, 6000)
    minuteurPlusLong = setTimeout(() => {
        plusLong.value = true
    }, DUREE_HABITUELLE_DEPASSEE_MS)
})
onUnmounted(() => {
    if (timer) clearInterval(timer)
    if (minuteurPlusLong) clearTimeout(minuteurPlusLong)
})
</script>

<template>
    <div class="optim-turpe-v2-loading flex flex-col gap-5">
        <!-- En-tête animé + étapes -->
        <div
            class="flex items-start gap-4 rounded-xl border border-primary-100 bg-gradient-to-br from-primary-50 to-surface-50 px-5 py-5"
        >
            <span
                class="mt-0.5 block h-9 w-9 shrink-0 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600"
                aria-hidden="true"
            />
            <div class="flex flex-col gap-3">
                <div>
                    <p class="text-base font-semibold text-primary-800">
                        {{ getTitre }}
                    </p>
                    <p class="text-xs text-primary-500">{{ t("subtitle") }}</p>
                </div>
                <ol class="flex flex-col gap-2">
                    <li
                        v-for="(s, i) in STEPS"
                        :key="s"
                        class="flex items-center gap-2 text-sm transition-colors duration-500"
                        :class="
                            i < current
                                ? 'text-primary-700'
                                : i === current
                                  ? 'font-medium text-primary-900'
                                  : 'text-primary-300'
                        "
                    >
                        <span
                            v-if="i < current"
                            class="pi pi-check-circle text-sm text-green-500"
                            aria-hidden="true"
                        />
                        <span
                            v-else-if="i === current"
                            class="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600"
                            aria-hidden="true"
                        />
                        <span
                            v-else
                            class="h-3.5 w-3.5 shrink-0 rounded-full border-2 border-primary-200"
                            aria-hidden="true"
                        />
                        {{ t(`steps.${s}`) }}
                    </li>
                </ol>
                <p
                    class="text-sm text-primary-700"
                    data-testid="optim-turpe-v2-attente-quitter"
                >
                    {{ getPhraseAttente }}
                </p>
            </div>
        </div>

        <!-- D29 : la dernière étude de l'ancienne version, déjà là pendant le calcul. -->
        <div
            v-if="ancienneVersion"
            class="flex flex-col gap-1 rounded-md border border-surface-200 bg-surface-0 px-4 py-3"
            data-testid="optim-turpe-v2-attente-v1"
        >
            <p class="text-sm font-medium text-primary-900">
                {{ ancienneVersion.ligne }}
            </p>
            <p class="text-xs text-primary-600">{{ ancienneVersion.suite }}</p>
        </div>

        <!-- Squelette du résultat (shimmer) — préfigure le bandeau de conclusion + les sections -->
        <div class="flex animate-pulse flex-col gap-4" aria-hidden="true">
            <div
                class="flex flex-col gap-3 rounded-md border border-surface-200 bg-surface-50 px-4 py-3"
            >
                <div class="flex items-center gap-3">
                    <div class="h-6 w-28 rounded-full bg-surface-200" />
                    <div class="h-7 w-40 rounded bg-surface-200" />
                </div>
                <div class="h-4 w-3/4 rounded bg-surface-200" />
                <div class="h-3 w-1/2 rounded bg-surface-100" />
            </div>
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div
                    class="h-28 rounded-md border border-surface-200 bg-surface-50"
                />
                <div
                    class="h-28 rounded-md border border-surface-200 bg-surface-50"
                />
            </div>
        </div>
    </div>
</template>
